import {
  Button,
  Flex,
  Grid,
  HStack,
  Modal,
  ModalBody,
  ModalContent,
  ModalOverlay,
  Text,
  useColorModeValue,
  useDisclosure,
} from '@chakra-ui/react';
import React from 'react';

import { useI18n } from 'lib/i18n/context';
import { LOCALE_LABELS, type Locale, SUPPORTED_LOCALES } from 'lib/i18n/core';
import IconSvg from 'ui/shared/IconSvg';

type Props = {
  isMobile?: boolean;
};

const LanguageSwitcher = ({ isMobile }: Props) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { locale, setLocale, t } = useI18n();

  const borderColor = useColorModeValue('#E9ECEF', '#222222');
  const bgColor = useColorModeValue('#FBFCFE', '#171717');
  const activeBgColor = useColorModeValue('#E8FBF6', '#222222');
  const hoverBgColor = useColorModeValue('#F4F5F6', '#222222');
  const textColor = useColorModeValue('#000000', '#FFFFFF');
  const secondaryTextColor = useColorModeValue('#4A5568', '#A0AEC0');
  const accentColor = '#14DCAC';

  const handleLocaleSelect = React.useCallback((nextLocale: Locale) => () => {
    setLocale(nextLocale);
    onClose();
  }, [ onClose, setLocale ]);

  return (
    <>
      <Button
        aria-label={ t('topBar.language') }
        onClick={ onOpen }
        minW={ isMobile ? '100%' : '40px' }
        w={ isMobile ? '100%' : '40px' }
        h={ isMobile ? '48px' : '40px' }
        p={ isMobile ? 0 : '10px' }
        borderRadius={ isMobile ? '4px' : '8px' }
        border={ isMobile ? 0 : '1px solid' }
        borderColor={ isMobile ? 'transparent' : borderColor }
        bg={ isMobile ? 'transparent' : bgColor }
        color={ textColor }
        fontSize="sm"
        fontWeight={ 500 }
        justifyContent={ isMobile ? 'space-between' : 'center' }
        _hover={{ bg: isMobile ? 'transparent' : hoverBgColor }}
        _active={{ bg: isMobile ? 'transparent' : hoverBgColor }}
      >
        { isMobile ? (
          <>
            <HStack spacing="12px">
              <IconSvg name="globe" boxSize="16px" transform="scale(1.25)" color={ textColor }/>
              <Text fontSize="14px" fontWeight={ 500 } lineHeight="16px" color={ textColor }>
                { t('topBar.language') }
              </Text>
            </HStack>
            <HStack spacing="8px">
              <Text fontSize="14px" fontWeight={ 500 } lineHeight="16px" color={ accentColor }>
                { LOCALE_LABELS[locale] }
              </Text>
              <IconSvg name="arrows/east-mini" boxSize={ 6 } color={ accentColor }/>
            </HStack>
          </>
        ) : (
          <IconSvg name="globe" boxSize={ 5 } transform="scale(1.67)"/>
        ) }
      </Button>

      <Modal isOpen={ isOpen } onClose={ onClose } isCentered motionPreset="none">
        <ModalOverlay bg="rgba(0, 0, 0, 0.3)" backdropFilter="blur(1px)"/>
        <ModalContent
          w="calc(100vw - 32px)"
          maxW="896px"
          maxH="min(calc(100vh - 2rem), 28rem)"
          p="24px"
          borderRadius="12px"
          border="1px solid"
          borderColor={ borderColor }
          bg={ bgColor }
          overflow="hidden"
          display="flex"
          flexDirection="column"
          mx={ 4 }
        >
          <ModalBody p={ 0 } display="flex" flexDirection="column" overflow="hidden">
            <Flex justifyContent="space-between" alignItems="center" mb={ 4 }>
              <Text fontSize="24px" fontWeight={ 500 } color={ textColor } lineHeight="1.2">
                { t('topBar.language') }
              </Text>
              <Button
                variant="ghost"
                onClick={ onClose }
                color={ secondaryTextColor }
                minW="auto"
                h="auto"
                p={ 1 }
                borderRadius="6px"
                _hover={{ color: textColor, bg: hoverBgColor }}
                aria-label="Close"
              >
                <IconSvg name="cross" boxSize={ 4 }/>
              </Button>
            </Flex>

            <Grid
              templateColumns={ isMobile ? '1fr' : 'repeat(4, minmax(0, 1fr))' }
              columnGap="16px"
              rowGap="16px"
              flex="1 1 auto"
              minH={ 0 }
              overflowY="auto"
              pb="24px"
              sx={ isMobile ? {
                paddingRight: '10px',
                scrollbarColor: `${ secondaryTextColor } transparent`,
                scrollbarWidth: 'thin',
              } : undefined }
            >
              { SUPPORTED_LOCALES.map((item) => (
                <Button
                  key={ item }
                  type="button"
                  justifyContent="flex-start"
                  variant="ghost"
                  w="100%"
                  h="52px"
                  borderRadius="8px"
                  fontSize="16px"
                  bg={ item === locale ? activeBgColor : 'transparent' }
                  color={ item === locale ? textColor : secondaryTextColor }
                  fontWeight={ item === locale ? 700 : 500 }
                  _hover={{ bg: item === locale ? activeBgColor : hoverBgColor, color: textColor }}
                  _active={{ bg: activeBgColor }}
                  onClick={ handleLocaleSelect(item) }
                >
                  { LOCALE_LABELS[item] }
                </Button>
              )) }
            </Grid>
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  );
};

export default React.memo(LanguageSwitcher);
